import { useState, useCallback } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

const MARGIN_MM = 10;
const LOGO_HEIGHT_MM = 10;
const LOGO_ASPECT = 272 / 45;

function getPageDims(doc) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  return { pageW: w, pageH: h, contentW: w - MARGIN_MM * 2 };
}

async function svgUrlToDataUrl(svgUrl) {
  const response = await fetch(svgUrl);
  const svgText = await response.text();
  const blob = new Blob([svgText], { type: "image/svg+xml" });
  const objectUrl = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const SCALE = 2;
      const w = (img.naturalWidth || 272) * SCALE;
      const h = (img.naturalHeight || 45) * SCALE;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Logo load failed"));
    };
    img.src = objectUrl;
  });
}

function addHeader(doc, title, { logoDataUrl, filters } = {}) {
  const { pageW, contentW } = getPageDims(doc);
  let y = MARGIN_MM;

  if (logoDataUrl) {
    const logoW = LOGO_HEIGHT_MM * LOGO_ASPECT;
    doc.addImage(logoDataUrl, "PNG", MARGIN_MM, y, logoW, LOGO_HEIGHT_MM);
    y += LOGO_HEIGHT_MM + 3;
  }

  const dateStr = new Date().toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  doc.setFontSize(14);
  doc.setTextColor(7, 74, 135);
  doc.text(title, MARGIN_MM, y + 5);

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Gerado em: ${dateStr}`, pageW - MARGIN_MM, y + 5, { align: "right" });

  doc.setDrawColor(7, 74, 135);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_MM, y + 8, pageW - MARGIN_MM, y + 8);
  y += 11;

  if (filters && filters.length > 0) {
    const filterText = filters.map((f) => `${f.label}: ${f.value}`).join("  |  ");
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    const lines = doc.splitTextToSize(filterText, contentW);
    doc.text(lines, MARGIN_MM, y + 3);
    y += lines.length * 4 + 5;

    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.2);
    doc.line(MARGIN_MM, y, pageW - MARGIN_MM, y);
    y += 3;
  } else {
    y += 3;
  }

  return y;
}

function addPageNumbers(doc) {
  const { pageW, pageH } = getPageDims(doc);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Página ${i} de ${totalPages}`,
      pageW / 2,
      pageH - 5,
      { align: "center" }
    );
  }
}

async function captureElement(element) {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
    windowWidth: element.scrollWidth,
  });
  return canvas;
}

async function addVisualSections(doc, refs, startY) {
  const { pageH, contentW } = getPageDims(doc);
  let yPos = startY;

  for (const ref of refs) {
    if (!ref.current) continue;

    const canvas = await captureElement(ref.current);
    const imgData = canvas.toDataURL("image/jpeg", 0.85);
    const imgHeightMm = (canvas.height * contentW) / canvas.width;

    const availableHeight = pageH - yPos - MARGIN_MM;

    doc.addImage(imgData, "JPEG", MARGIN_MM, yPos, contentW, imgHeightMm);

    if (imgHeightMm > availableHeight) {
      let consumed = availableHeight;
      const fullPage = pageH - 2 * MARGIN_MM;
      while (consumed < imgHeightMm) {
        doc.addPage();
        doc.addImage(
          imgData,
          "JPEG",
          MARGIN_MM,
          MARGIN_MM - consumed,
          contentW,
          imgHeightMm
        );
        consumed += fullPage;
      }
    }

    yPos += imgHeightMm + 6;
  }

  return yPos;
}

function addDataTable(doc, { columns, rows, startY }) {
  const { pageH } = getPageDims(doc);
  const needsNewPage = startY > pageH - 60;

  if (needsNewPage) {
    doc.addPage();
    startY = MARGIN_MM + 4;
  }

  autoTable(doc, {
    startY,
    head: [columns],
    body: rows,
    margin: { left: MARGIN_MM, right: MARGIN_MM },
    styles: {
      fontSize: 7,
      cellPadding: 2,
      overflow: "linebreak",
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [7, 74, 135],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7.5,
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },
    columnStyles: {
      0: { cellWidth: "auto" },
    },
  });
}

export function usePdfExport() {
  const [exportingPdf, setExportingPdf] = useState(false);

  const exportPdf = useCallback(
    async (
      visualRefs,
      { title, filename, tableData, orientation = "portrait", logoUrl, filters }
    ) => {
      setExportingPdf(true);

      try {
        const doc = new jsPDF({
          orientation,
          unit: "mm",
          format: "a4",
        });

        let logoDataUrl = null;
        if (logoUrl) {
          try {
            logoDataUrl = await svgUrlToDataUrl(logoUrl);
          } catch (e) {
            console.warn("Falha ao carregar logo para PDF:", e);
          }
        }

        const yPos = addHeader(doc, title, { logoDataUrl, filters });

        const validRefs = visualRefs.filter((r) => r.current);
        let nextY = yPos;
        if (validRefs.length > 0) {
          nextY = await addVisualSections(doc, validRefs, yPos);
        }

        if (tableData && tableData.rows.length > 0) {
          addDataTable(doc, {
            columns: tableData.columns,
            rows: tableData.rows,
            startY: nextY,
          });
        }

        addPageNumbers(doc);
        doc.save(filename);
        return true;
      } catch (error) {
        console.error("Erro ao exportar PDF:", error);
        throw error;
      } finally {
        setExportingPdf(false);
      }
    },
    []
  );

  return { exportPdf, exportingPdf };
}
